/**
 * Demo Portfolio Data
 *
 * Realistic portfolio with selected tokens, transaction history, and swaps
 * Used to showcase all features without requiring real user data
 */

import type { Token, Position } from '../types';

// Helper to generate dates relative to today
const daysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

// Swap chain IDs for tracking
const SWAP_CHAINS = {
  BTC_TO_HBAR: 'swap-chain-btc-hbar-001',
  XRP_TO_ONDO: 'swap-chain-xrp-ondo-001',
  LINK_TO_PYTH: 'swap-chain-link-pyth-001',
  CRO_TO_AXL: 'swap-chain-cro-axl-001',
};

export const DEMO_PORTFOLIO: Token[] = [
  // Bitcoin - Main holding with DCA strategy + partial swap to HBAR
  {
    id: 'bitcoin',
    displayName: 'Bitcoin',
    positions: [
      { id: 'btc-1', type: 'buy', date: daysAgo(365), quantity: 0.5, price: 26500, note: 'First BTC purchase' },
      { id: 'btc-2', type: 'buy', date: daysAgo(300), quantity: 0.25, price: 29000, note: 'DCA' },
      { id: 'btc-3', type: 'buy', date: daysAgo(180), quantity: 0.15, price: 42000, note: 'DCA' },
      { id: 'btc-4', type: 'buy', date: daysAgo(90), quantity: 0.2, price: 67000, note: 'Post-halving accumulation' },
      { id: 'btc-5', type: 'sell', date: daysAgo(30), quantity: 0.1, price: 95000, note: 'Partial profit taking' },
      // Swap BTC to HBAR
      {
        id: 'btc-6',
        type: 'sell',
        date: daysAgo(75),
        quantity: 0.05,
        price: 72000,
        note: 'Swapped to HBAR',
        is_swap: true,
        swap_chain_id: SWAP_CHAINS.BTC_TO_HBAR
      },
    ],
    tags: ['Store of Value', 'DCA'],
    targetPrice: 150000,
    note: 'Main BTC holding - Long term DCA strategy'
  },

  // Hedera - HBAR (includes swap from BTC)
  {
    id: 'hedera-hashgraph',
    displayName: 'Hedera',
    positions: [
      { id: 'hbar-1', type: 'buy', date: daysAgo(400), quantity: 50000, price: 0.05, note: 'Enterprise blockchain play' },
      { id: 'hbar-2', type: 'buy', date: daysAgo(250), quantity: 25000, price: 0.07, note: 'Added more' },
      { id: 'hbar-3', type: 'staking', date: daysAgo(200), quantity: 5000, price: 0, staking_apy: 6.5, staking_platform: 'Native', staking_is_locked: false, note: 'Staking rewards' },
      // Received from BTC swap - preserves original cost basis!
      {
        id: 'hbar-4',
        type: 'buy',
        date: daysAgo(75),
        quantity: 45000,
        price: 0.08,
        note: 'Swapped from 0.05 BTC',
        is_swap: true,
        swap_from_position_id: 'btc-6',
        swap_chain_id: SWAP_CHAINS.BTC_TO_HBAR,
        original_cost_basis: 1325 // 0.05 BTC * $26500 original cost
      },
    ],
    tags: ['Enterprise', 'DAG', 'Staking'],
    targetPrice: 0.50,
    note: 'Hedera Hashgraph - Enterprise DLT'
  },

  // Flux - Decentralized cloud
  {
    id: 'zelcash',
    displayName: 'Flux',
    positions: [
      { id: 'flux-1', type: 'buy', date: daysAgo(400), quantity: 5000, price: 0.35, note: 'Web3 cloud infrastructure' },
      { id: 'flux-2', type: 'buy', date: daysAgo(200), quantity: 3000, price: 0.55, note: 'Added before node setup' },
      { id: 'flux-3', type: 'mining', date: daysAgo(150), quantity: 800, price: 0, mining_pool: 'FluxPools', mining_hashrate: 120, note: 'Mining rewards' },
      { id: 'flux-4', type: 'mining', date: daysAgo(90), quantity: 750, price: 0, mining_pool: 'FluxPools', mining_hashrate: 120, note: 'Mining rewards' },
    ],
    tags: ['Cloud', 'Infrastructure', 'Mining', 'FluxNode'],
    targetPrice: 3.00,
    note: 'Running a STRATUS FluxNode - Web3 cloud play'
  },

  // Quant Network - QNT
  {
    id: 'quant-network',
    displayName: 'Quant',
    positions: [
      { id: 'qnt-1', type: 'buy', date: daysAgo(350), quantity: 25, price: 85, note: 'Overledger interoperability' },
      { id: 'qnt-2', type: 'buy', date: daysAgo(180), quantity: 15, price: 105, note: 'DCA' },
    ],
    tags: ['Enterprise', 'Interoperability', 'CBDC'],
    targetPrice: 500,
    note: 'Overledger - Enterprise blockchain interoperability'
  },

  // Crypto.com - CRO (with partial swap to AXL)
  {
    id: 'crypto-com-chain',
    displayName: 'Cronos',
    positions: [
      { id: 'cro-1', type: 'buy', date: daysAgo(500), quantity: 10000, price: 0.12, note: 'Card staking' },
      { id: 'cro-2', type: 'staking', date: daysAgo(400), quantity: 2000, price: 0, staking_apy: 10, staking_platform: 'Crypto.com', staking_is_locked: true, staking_duration: 180, note: 'Card tier staking' },
      // Swap CRO to AXL for interop exposure
      {
        id: 'cro-3',
        type: 'sell',
        date: daysAgo(100),
        quantity: 3000,
        price: 0.09,
        note: 'Swapped to AXL',
        is_swap: true,
        swap_chain_id: SWAP_CHAINS.CRO_TO_AXL
      },
    ],
    tags: ['Exchange', 'Staking', 'Card'],
    targetPrice: 0.30,
    note: 'Crypto.com ecosystem - Card benefits'
  },

  // ONDO Finance (includes swap from XRP)
  {
    id: 'ondo-finance',
    displayName: 'Ondo',
    positions: [
      { id: 'ondo-1', type: 'buy', date: daysAgo(200), quantity: 2000, price: 0.25, note: 'RWA tokenization leader' },
      { id: 'ondo-2', type: 'buy', date: daysAgo(90), quantity: 1500, price: 0.85, note: 'Added on RWA narrative' },
      // Received from XRP swap
      {
        id: 'ondo-3',
        type: 'buy',
        date: daysAgo(50),
        quantity: 1200,
        price: 1.10,
        note: 'Swapped from 2000 XRP',
        is_swap: true,
        swap_from_position_id: 'xrp-3',
        swap_chain_id: SWAP_CHAINS.XRP_TO_ONDO,
        original_cost_basis: 900 // 2000 XRP * $0.45 original cost
      },
    ],
    tags: ['RWA', 'DeFi', 'Tokenization'],
    targetPrice: 5.00,
    note: 'Real World Assets tokenization - BlackRock partnership'
  },

  // Constellation - DAG
  {
    id: 'constellation-labs',
    displayName: 'Constellation',
    positions: [
      { id: 'dag-1', type: 'buy', date: daysAgo(380), quantity: 100000, price: 0.03, note: 'DAG technology play' },
      { id: 'dag-2', type: 'buy', date: daysAgo(200), quantity: 50000, price: 0.05, note: 'US Gov partnerships' },
    ],
    tags: ['DAG', 'Big Data', 'Government'],
    targetPrice: 0.25,
    note: 'Constellation Network - Big data validation'
  },

  // Chainlink - LINK (with partial swap to PYTH)
  {
    id: 'chainlink',
    displayName: 'Chainlink',
    positions: [
      { id: 'link-1', type: 'buy', date: daysAgo(400), quantity: 200, price: 6.5, note: 'Oracle infrastructure' },
      { id: 'link-2', type: 'buy', date: daysAgo(200), quantity: 100, price: 14, note: 'CCIP launch' },
      { id: 'link-3', type: 'staking', date: daysAgo(100), quantity: 50, price: 0, staking_apy: 4.75, staking_platform: 'Chainlink Staking v0.2', staking_is_locked: true, note: 'Staking rewards' },
      // Swap some LINK to PYTH (Oracle diversification)
      {
        id: 'link-4',
        type: 'sell',
        date: daysAgo(45),
        quantity: 50,
        price: 18,
        note: 'Swapped to PYTH - Oracle diversification',
        is_swap: true,
        swap_chain_id: SWAP_CHAINS.LINK_TO_PYTH
      },
    ],
    tags: ['Oracles', 'Infrastructure', 'CCIP', 'Staking'],
    targetPrice: 50,
    note: 'Critical DeFi infrastructure - Oracles & CCIP'
  },

  // Pyth Network (includes swap from LINK)
  {
    id: 'pyth-network',
    displayName: 'Pyth Network',
    positions: [
      { id: 'pyth-1', type: 'buy', date: daysAgo(150), quantity: 5000, price: 0.35, note: 'Solana oracle play' },
      { id: 'pyth-2', type: 'buy', date: daysAgo(60), quantity: 3000, price: 0.45, note: 'Cross-chain expansion' },
      // Received from LINK swap
      {
        id: 'pyth-3',
        type: 'buy',
        date: daysAgo(45),
        quantity: 2000,
        price: 0.45,
        note: 'Swapped from 50 LINK',
        is_swap: true,
        swap_from_position_id: 'link-4',
        swap_chain_id: SWAP_CHAINS.LINK_TO_PYTH,
        original_cost_basis: 325 // 50 LINK * $6.5 original cost
      },
    ],
    tags: ['Oracles', 'Solana', 'DeFi'],
    targetPrice: 2.00,
    note: 'High-frequency oracle network'
  },

  // XRP - Ripple (with partial swap to ONDO)
  {
    id: 'ripple',
    displayName: 'XRP',
    positions: [
      { id: 'xrp-1', type: 'buy', date: daysAgo(500), quantity: 5000, price: 0.45, note: 'Cross-border payments' },
      { id: 'xrp-2', type: 'buy', date: daysAgo(200), quantity: 3000, price: 0.55, note: 'Post-SEC clarity' },
      // Swap XRP to ONDO (Payments to RWA rotation)
      {
        id: 'xrp-3',
        type: 'sell',
        date: daysAgo(50),
        quantity: 2000,
        price: 0.65,
        note: 'Swapped to ONDO - RWA rotation',
        is_swap: true,
        swap_chain_id: SWAP_CHAINS.XRP_TO_ONDO
      },
    ],
    tags: ['Payments', 'Cross-border', 'Banking'],
    targetPrice: 3.00,
    note: 'Ripple - Institutional cross-border payments'
  },

  // Stellar - XLM
  {
    id: 'stellar',
    displayName: 'Stellar',
    positions: [
      { id: 'xlm-1', type: 'buy', date: daysAgo(400), quantity: 20000, price: 0.10, note: 'Remittances play' },
      { id: 'xlm-2', type: 'buy', date: daysAgo(150), quantity: 10000, price: 0.12, note: 'USDC on Stellar' },
    ],
    tags: ['Payments', 'Remittances', 'CBDC'],
    targetPrice: 0.50,
    note: 'Stellar - Financial inclusion & remittances'
  },

  // AAVE
  {
    id: 'aave',
    displayName: 'Aave',
    positions: [
      { id: 'aave-1', type: 'buy', date: daysAgo(350), quantity: 15, price: 65, note: 'DeFi lending leader' },
      { id: 'aave-2', type: 'buy', date: daysAgo(100), quantity: 10, price: 95, note: 'GHO stablecoin launch' },
    ],
    tags: ['DeFi', 'Lending', 'GHO'],
    targetPrice: 500,
    note: 'Leading DeFi lending protocol - GHO stablecoin'
  },

  // Axelar - AXL (includes swap from CRO)
  {
    id: 'axelar',
    displayName: 'Axelar',
    positions: [
      { id: 'axl-1', type: 'buy', date: daysAgo(280), quantity: 3000, price: 0.40, note: 'Cross-chain messaging' },
      { id: 'axl-2', type: 'buy', date: daysAgo(120), quantity: 2000, price: 0.85, note: 'Squid Router integration' },
      // Received from CRO swap
      {
        id: 'axl-3',
        type: 'buy',
        date: daysAgo(100),
        quantity: 350,
        price: 0.77,
        note: 'Swapped from 3000 CRO',
        is_swap: true,
        swap_from_position_id: 'cro-3',
        swap_chain_id: SWAP_CHAINS.CRO_TO_AXL,
        original_cost_basis: 360 // 3000 CRO * $0.12 original cost
      },
    ],
    tags: ['Interoperability', 'Cross-chain', 'Cosmos'],
    targetPrice: 5.00,
    note: 'Cross-chain communication protocol'
  },

  // Render Network - RNDR (AI & GPU rendering)
  {
    id: 'render-token',
    displayName: 'Render',
    positions: [
      { id: 'rndr-1', type: 'buy', date: daysAgo(200), quantity: 150, price: 2.50, note: 'AI narrative play' },
      { id: 'rndr-2', type: 'buy', date: daysAgo(60), quantity: 100, price: 5.80, note: 'Added on GPU demand' },
    ],
    tags: ['AI', 'GPU', 'Rendering'],
    targetPrice: 25,
    note: 'Decentralized GPU rendering network - AI infrastructure'
  },

  // Injective Protocol - INJ
  {
    id: 'injective-protocol',
    displayName: 'Injective',
    positions: [
      { id: 'inj-1', type: 'buy', date: daysAgo(180), quantity: 30, price: 8.50, note: 'DeFi derivatives play' },
      { id: 'inj-2', type: 'buy', date: daysAgo(45), quantity: 20, price: 22.00, note: 'Cosmos ecosystem growth' },
    ],
    tags: ['DeFi', 'Derivatives', 'Cosmos'],
    targetPrice: 100,
    note: 'Decentralized derivatives exchange - Layer 1'
  },
];

// Calculate some demo stats
export const DEMO_STATS = {
  totalTokens: DEMO_PORTFOLIO.length,
  totalPositions: DEMO_PORTFOLIO.reduce((acc, t) => acc + t.positions.length, 0),
  swapChains: Object.keys(SWAP_CHAINS).length,
  oldestPosition: '~1.5 years ago',
  diversification: 'Diversified across L1, Oracles, RWA, DeFi, Payments, AI, and Cross-chain',
  features: ['DCA', 'Staking', 'Mining', 'Swaps with cost basis tracking', 'Target prices']
};
